import { RouteObject } from "react-router-dom";
import AppLayout from "./shared/AppLayout";
import ClientMinimalHeaderLayout from "./shared/ClientMinimalHeaderLayout";
import ProtectedRouteShell from "./shared/ProtectedRouteShell";
import { Home } from "./features/Home";
import { NotificationProvider } from "./features/Notifications";
import CEONote from "./features/CEONote";
import UnAuthorized from "./components/UnAuthorized";
import RequireRole from "./shared/RequireRole";
import { RoleNames } from "./app/constants/roles";

export const routes: RouteObject[] = [
  {
    path: "/",
    async lazy() {
      const { LandingPage } = await import("./features/LandingPage");
      return { element: <LandingPage /> };
    }
  },
  {
    path: "profile-setup",
    async lazy() {
      const { ProfileSetup } = await import("./features/ProfileSetup");
      return { element: <ProfileSetup /> };
    }
  },
  {
    path: "contact-support",
    async lazy() {
      const { ContactSupport } = await import("./features/ContactSupport");
      return { element: <ContactSupport /> };
    }
  },
  {
    path: "unauthorized",
    element: <UnAuthorized />
  },
  {
    path: "/:id",
    async lazy() {
      const { LandingArticleDetails } = await import("./features/LandingPage");
      return { element: <LandingArticleDetails /> };
    }
  },
  {
    element: (
      <NotificationProvider>
        <AppLayout />
      </NotificationProvider>
    ),
    children: [
      {
        path: "terms-of-use",
        async lazy() {
          const TermsOfUse = (await import(
            "./features/Thoughtware/Instructions/TermsOfUse"
          )).default;
          return { element: <TermsOfUse /> };
        }
      },
      {
        path: "privacy-policy",
        async lazy() {
          const PrivacyPolicy = (await import(
            "./features/Thoughtware/Instructions/PrivacyPolicy"
          )).default;
          return { element: <PrivacyPolicy /> };
        }
      },
      {
        path: "disclaimer",
        async lazy() {
          const Disclaimer = (await import(
            "./features/Thoughtware/Instructions/Disclaimer"
          )).default;
          return { element: <Disclaimer /> };
        }
      }
    ]
  },

  {
    element: (
      <ProtectedRouteShell>
        <NotificationProvider>
          <AppLayout />
        </NotificationProvider>
      </ProtectedRouteShell>
    ),
    children: [
      {
        path: "home",
        element: <Home />
      },
      {
        path: "MessageDetails",
        async lazy() {
          const { MessageDetails } = await import(
            "./features/Home/"
          );
          return { element: <MessageDetails /> };
        }
      },
      {
        path: "alumini-directory",
        async lazy() {
          const { AluminiDirectory } = await import(
            "./features/AluminiDirectory"
          );
          return { element: <AluminiDirectory /> };
        }
      },
      {
        path: "alumini-directory/:employeeId",
        async lazy() {
          const { ProfileView } = await import(
            "./features/AluminiDirectory"
          );
          return { element: <ProfileView /> };
        }
      },
      {
        path: "profile",
        async lazy() {
          const { ProfileView } = await import(
            "./features/AluminiDirectory"
          );
          return { element: <ProfileView /> };
        }
      },
      {
        path: "insights",
        async lazy() {
          const { Thoughtware } = await import(
            "./features/Thoughtware"
          );
          return { element: <Thoughtware /> };
        }
      },
      {
        path: "insights-detail/:postId",
        async lazy() {
          const { ThoughtwareDetails } = await import(
            "./features/Thoughtware"
          );
          return { element: <ThoughtwareDetails /> };
        }
      },
      {
        path: "events",
        async lazy() {
          const { Events } = await import("./features/Events");
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Alumni]}>
              <Events />
            </RequireRole>
          ) };
        }
      },
      {
        path: "events/:id",
        async lazy() {
          const EventsDetails = (await import(
            "./features/Events/EventsDetails"
          )).default;
          return { element: <EventsDetails /> };
        }
      },
      {
        path: "helpdesk",
        async lazy() {
          const { HelpDesk } = await import(
            "./features/HelpDesk"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Alumni]}>
              <HelpDesk />
            </RequireRole>
          ) };
        }
      },
      {
        path: "conversations",
        async lazy() {
          const { Conversation } = await import("./features/Conversation");
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Alumni]}>
              <Conversation />
            </RequireRole>
          ) };
        }
      },
      {
        path: "notifications",
        async lazy() {
          const { Notifications } = await import(
            "./features/Notifications"
          );
          return { element: <Notifications /> };
        }
      },
      {
        path:'ceo-notes',
        element: <CEONote />
      },
      {
        path: "marketplace-jobs",
        async lazy() {
          const { JobMarketplace } = await import("./features/JobMarketplace");
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Alumni]}>
              <JobMarketplace />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/marketplaceJobs",
        async lazy() {
          const { MarketplaceJobs } = await import(
            "./features/Admin/MarketplaceJobs"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <MarketplaceJobs />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/marketplaceJobs/ViewJobDetails",
        async lazy() {
          const { ViewJobDetails } = await import(
            "./features/Admin/MarketplaceJobs"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ViewJobDetails />
            </RequireRole>
          ) };
        }
      }, 
      
      {
        path: "admin/marketplaceJobs/ApplicantsList",
        async lazy() {
          const { ApplicantsList } = await import(
            "./features/Admin/MarketplaceJobs"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ApplicantsList />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/marketplaceJobs/PastApplicantsList",
        async lazy() {
          const { PastApplicantsList } = await import(
            "./features/Admin/MarketplaceJobs"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <PastApplicantsList />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/impersonate",
        async lazy() {
          const { ImpersonateUser } = await import(
            "./features/Admin"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ImpersonateUser />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/alumniDirectory",
        async lazy() {
          const { AlumniDirectory } = await import(
            "./features/Admin/alumniDirectory"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <AlumniDirectory />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/alumniDirectory/:employeeId",
        async lazy() {
          const { ProfileView } = await import(
            "./features/AluminiDirectory"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ProfileView />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/Conversation",
        async lazy() {
          const { Conversation } = await import(
            "./features/Admin/Conversation"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <Conversation />
            </RequireRole>
          ) };
        }
      }, 
      {
        path: "admin/HomeConfigurator",
        async lazy() {
          const { HomeConfigurator } = await import(
            "./features/Admin/HomeConfigurator"
          );
          return { element: <HomeConfigurator /> };
        }
      }, 
      {
        path: "marketplace-jobs/:jobTitle",
        async lazy() {
          const { JobDetail } = await import(
            "./features/JobMarketplace"
          );
          return { element: <JobDetail /> };
        }
      },
      {
        path: "dashboard",
        async lazy() {
          const { Dashboard } = await import(
            "./features/Admin/Dashboard"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <Dashboard />
            </RequireRole>
          ) };
        }
      },
       {
        path: "dashboard/view-request/:employeeId",
        async lazy() {
          const { ViewRequest } = await import(
            "./features/Admin/Dashboard"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ViewRequest />
            </RequireRole>
          ) };
        }
      },
      {
        path: "dashboard/acknowledge/:requestId",
        async lazy() {
          const { Acknowledge } = await import(
            "./features/Admin/Dashboard"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <Acknowledge />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/content-management",
        async lazy() {
          const { ContentManagement } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ContentManagement />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-article",
        async lazy() {
          const { NewArticle } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewArticle />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-alumni-spotlight",
        async lazy() {
          const { NewAlumniSpotlight } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewAlumniSpotlight />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-events",
        async lazy() {
          const { NewEvents } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewEvents />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-reports",
        async lazy() {
          const { NewReports } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewReports />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-survey",
        async lazy() {
          const { NewSurvey } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewSurvey />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/new-video",
        async lazy() {
          const { NewVideo } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewVideo />
            </RequireRole>
          ) };
        }
      },
       {
        path: "admin/new-jobs",
        async lazy() {
          const { NewJobs } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <NewJobs />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/leaders-message",
        async lazy() {
          const { LeadersMessage } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <LeadersMessage />
            </RequireRole>
          ) };
        }
      },
      {
        path: "admin/view-content-details",
        async lazy() {
          const { ViewContentDetailsPage } = await import(
            "./features/Admin/ContentManagement"
          );
          return { element: (
            <RequireRole allowedRoles={[RoleNames.Admin]}>
              <ViewContentDetailsPage />
            </RequireRole>
          ) };
        }
      },
    ]
  },
  {
    element: <ClientMinimalHeaderLayout />,
    children: [
      {
        path: "marketplace-jobs/post-job",
        async lazy() {
          const { PostJob } = await import("./features/JobMarketplace");
          return { element: <PostJob /> };
        }
      }
    ]
  }
];